import React from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Hospital, TrendingUp, Zap } from "lucide-react";
import HppyfxMark from "@/components/HppyfxMark";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * Emergent Google OAuth sign-in. Redirects to Emergent auth with current origin
 * as the callback — after Google consent, user lands back at window.location.origin
 * with #session_id=... which AuthCallback.jsx consumes.
 */
const SORA_FONT = "'Sora', ui-sans-serif, system-ui, -apple-system, sans-serif";

export default function Login() {
  const [loading, setLoading] = React.useState(false);

  const handleGoogleSignIn = () => {
    setLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" +
      encodeURIComponent(redirectUrl);
  };

  return (
    <div
      className="min-h-dvh flex flex-col bg-[#0A0A0A] bg-cover bg-center bg-no-repeat lg:h-svh lg:max-h-svh lg:flex-row lg:overflow-hidden"
      style={{
        backgroundImage: "url('/HppyFX-background.png')",
        fontFamily: SORA_FONT,
      }}
      data-testid="login-screen"
    >
      {/* Left brand panel — centered, compact scale so it fits one viewport */}
      <div className="hidden lg:flex lg:w-1/2 lg:min-h-0 text-white flex-col relative lg:overflow-y-auto">
        <div className="flex flex-1 flex-col items-center justify-center px-8 xl:px-12 py-8 w-full relative z-10">
          <div className="w-full max-w-xl xl:max-w-2xl flex flex-col items-start text-left">
            <div className="flex flex-col items-start gap-5 w-full">
              <div className="flex flex-col items-start gap-2">
                <HppyfxMark
                  variant="inverted"
                  className="h-16 xl:h-[5.5rem] w-auto"
                />
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                  Hospital Economics Engine
                </div>
              </div>
              <div className="inline-flex items-center self-start min-h-10 px-5 py-2 rounded-lg bg-[rgba(245,166,35,0.2)] border border-[#f79e1b]">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white font-medium">
                  / For C-Suite Leaders
                </span>
              </div>
              <h1
                className="font-display text-3xl xl:text-4xl font-black tracking-tighter leading-[1.08] max-w-lg text-left"
                style={{ fontFamily: SORA_FONT }}
              >
                Revenue leakage, cost pressure,
                <br />
                <span className="text-[#f5a623]">solved in 60 seconds.</span>
              </h1>
              <p className="text-white/85 leading-relaxed text-sm xl:text-base max-w-lg text-left">
                The AI forecasting agent that tells CEOs, CFOs and COOs exactly
                where money is made, lost, and left on the table — without
                compromising care.
              </p>
              <div className="mt-1 grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-xl xl:max-w-2xl">
                {[
                  [
                    TrendingUp,
                    "Revenue intelligence",
                    "Payer mix · claims leakage",
                  ],
                  [
                    Zap,
                    "Cost & resource pressure",
                    "OT · procurement · staffing",
                  ],
                  [Hospital, "C-suite insights", "Written like an economist"],
                  [
                    ShieldCheck,
                    "Secure by default",
                    "Google OAuth · httpOnly cookies",
                  ],
                ].map(([Ic, t, s]) => (
                  <div
                    key={t}
                    className="rounded-[14px] border border-white/30 bg-[#1a325e] p-4 sm:p-5 text-left flex flex-col gap-2"
                  >
                    <Ic
                      className="w-8 h-8 text-[#f5a623] shrink-0"
                      strokeWidth={1.75}
                    />
                    <div className="text-base sm:text-lg font-semibold text-white leading-snug tracking-tight">
                      {t}
                    </div>
                    <div className="text-xs sm:text-sm text-white/80 leading-relaxed">
                      {s}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div
          className="relative z-10 shrink-0 py-4 px-8 xl:px-12 text-left text-xs text-white/40 font-mono-metric"
          style={{ fontFamily: SORA_FONT }}
        >
          © 2026 HppyFx · Prototype V3
        </div>
      </div>

      {/* Right: Google sign-in — bordered card (Figma node 25:83) */}
      <div
        className="w-full flex-1 lg:flex-none lg:w-1/2 lg:min-h-0 flex items-center justify-center p-6 sm:p-8 lg:overflow-y-auto"
        style={{
          background:
            "linear-gradient(165deg, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0.52) 100%)",
        }}
      >
        <div className="w-full max-w-[643px] flex flex-col items-center">
          <div className="lg:hidden mb-10 w-full max-w-md">
            <HppyfxMark variant="default" />
          </div>

          <div
            className="w-full max-w-md rounded-[28px] border border-[#f5a623] bg-[#0d1328] shadow-[0px_0px_8px_4px_rgba(245,166,35,0.1)] px-8 py-10 sm:px-10 sm:py-12"
            data-testid="login-signin-card"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#f5a623] font-bold mb-4">
              / Sign in
            </div>
            <h2
              className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-none mb-3 text-white"
              style={{ fontFamily: SORA_FONT }}
            >
              Enter the command center
            </h2>
            <p className="text-sm text-white/55 mb-10 max-w-sm leading-relaxed">
              One-click sign-in with your Google account. HppyFx stores only
              your email and display name — no PHI, no hospital data, no
              passwords.
            </p>

            <Button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#f9a825] hover:bg-[#e69a18] text-[#0A0A0A] border border-[#f9a825] font-medium tracking-wide flex items-center justify-center gap-3"
              data-testid="google-signin-btn"
            >
              <GoogleIcon />
              {loading ? "Redirecting to Google..." : "Continue with Google"}
            </Button>

            <div className="mt-12 pt-8 border-t border-white/15">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/45 mb-4">
                / What you get after sign-in
              </div>
              <ol className="space-y-3 text-sm">
                {[
                  "Choose demo hospital data or upload CSV",
                  "5-tab executive dashboard generated in seconds",
                  "9 economist insights + what-if scenarios",
                ].map((t, i) => (
                  <li key={t} className="flex gap-3">
                    <span
                      className="font-mono-metric text-xs text-[#f5a623] font-bold pt-0.5"
                      style={{ fontFamily: SORA_FONT }}
                    >
                      0{i + 1}
                    </span>
                    <span className="text-white/70">{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.35 0-4.35-1.59-5.06-3.72H.92v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.94 10.7a5.41 5.41 0 0 1 0-3.4V4.97H.92a9 9 0 0 0 0 8.06l3.02-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0a9 9 0 0 0-8.08 4.97L3.94 7.3C4.65 5.17 6.65 3.58 9 3.58z"
      />
    </svg>
  );
}
